import React, {useEffect, useRef, useCallback, useState} from 'react';
import { EntryModel, JournalData, JournalPageData, EntryType } from 'lib/models';
import EntryContentContainer from 'components/MoveableEntry/EntryContentContainer';
import { updateEntry, createEntry, getEntry } from "lib/api";
import { formatDateToYYYYMMDD } from 'lib/format';
import EditableTitle from 'components/EditableTitle';
import DateBar from './DateBar';
import TextEditor from "components/TextEditor";

interface JournalEntryProps {
  refId: string;
  model: EntryModel<JournalData>;
}

function JournalEntry({model}: JournalEntryProps) {
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [initialText, setInitialText] = useState<string|undefined>(undefined);

  const currentPage = useRef<EntryModel<JournalPageData>|undefined>(undefined);
  const pageUuidsByDate = useRef(model.data.pageUuidsByDate);
  const isInitialLoad = useRef(true);

  const setPage = (page: EntryModel<JournalPageData>|null) => {
    if (page) {
      currentPage.current = page;
      setInitialText(page.data.text);
    } else {
      currentPage.current = undefined;
      setInitialText(undefined);
    }
  };

  useEffect(() => {
    const loadCurrentPage = async () => {
      if(!isInitialLoad.current) return;
      isInitialLoad.current = false;

      const dateId = formatDateToYYYYMMDD(currentDate);
      if (pageUuidsByDate.current) {
        if (dateId in pageUuidsByDate.current) {
          const currentPageUuid = pageUuidsByDate.current[dateId];
          const page = await getEntry(currentPageUuid, EntryType.JOURNAL_PAGE);
          setPage(page as EntryModel<JournalPageData>);
        } else {
          setPage(null);
        }
      }
    };
    loadCurrentPage();
  }, [currentDate]);


  const addPage = useCallback(async (
    pageDate: Date,
    initialText: string = "",
  ): Promise<void> => {
      const dateId = formatDateToYYYYMMDD(pageDate);
      const initialPageData: Partial<JournalPageData> = {
        journalUuid: model.uuid,
        text: initialText,
      };
      const page = await createEntry(EntryType.JOURNAL_PAGE, initialPageData);
      pageUuidsByDate.current[dateId] = page.uuid;
      const updatePatch: Partial<JournalData> = { pageUuidsByDate: pageUuidsByDate.current };
      await updateEntry(model.uuid, model.data.type, updatePatch);
      setPage(page as EntryModel<JournalPageData>);
  }, []);

  const saveTitle = useCallback(async (title: string = ""): Promise<void> => {
      const updatePatch: Partial<JournalData> = {title};
      await updateEntry(model.uuid, model.data.type, updatePatch);
  }, []);

  const savePageText = useCallback(async (date: Date, updatedText: string): Promise<void> => {
    if (!updatedText) return;
    if (currentPage.current) {
      const updatePatch: Partial<JournalPageData> = {text: updatedText};
      await updateEntry(currentPage.current.uuid, currentPage.current.data.type, updatePatch);
      currentPage.current.data.text = updatedText;
    } else {
      addPage(date, updatedText);
    }
  }, [model]);

  return (
    <div className={`w-full h-full overflow-auto break-words flex flex-col p-2 bg-blue-400 rounded-lg`} >
      <EditableTitle
        initialText={model.data.title || "Journal"}
        onTitleChange={saveTitle}
      />
      <EntryContentContainer className="flex-grow">
        <TextEditor
          initialText={initialText || ""}
          onChangeThrottled={(updatedText) => savePageText(currentDate, updatedText)}
          placeholderText={"Share your thoughts..."}
        />
      </EntryContentContainer>
      <DateBar
        date={currentDate}
        onDateChange={(date) => setCurrentDate(date)}
      />
    </div>
  );
}

export default JournalEntry;
